import { useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  CURRENCY,
  todayIso,
  type GlBalanceSheetResponse,
  type GlIncomeStatementResponse,
} from "@/lib/gl";

function fmt(n: number) {
  if (Math.abs(n) < 0.005) return `${CURRENCY} 0.00`;
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export default function GeneralLedgerStatements() {
  const [tab, setTab] = useState("income");
  const [from, setFrom] = useState(startOfYear());
  const [to, setTo] = useState(todayIso());
  const [asOf, setAsOf] = useState(todayIso());

  const [income, setIncome] = useState<GlIncomeStatementResponse | null>(null);
  const [bs, setBs] = useState<GlBalanceSheetResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const loadIncome = async () => {
    setLoading(true);
    try {
      const d = await api.get<GlIncomeStatementResponse>(
        `/gl/reports/income-statement?from=${from}&to=${to}`,
      );
      setIncome(d);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load income statement");
    } finally {
      setLoading(false);
    }
  };

  const loadBs = async () => {
    setLoading(true);
    try {
      const d = await api.get<GlBalanceSheetResponse>(
        `/gl/reports/balance-sheet?as_of=${asOf}`,
      );
      setBs(d);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load balance sheet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "income") loadIncome();
    else loadBs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="income">Income Statement</TabsTrigger>
          <TabsTrigger value="bs">Balance Sheet</TabsTrigger>
        </TabsList>

        <TabsContent value="income" className="mt-4">
          <Card className="shadow-card">
            <CardHeader className="flex flex-row items-end justify-between gap-3 space-y-0 flex-wrap">
              <div>
                <CardTitle className="text-lg">Income Statement</CardTitle>
                <CardDescription>
                  Revenue and expense activity over a date range.
                </CardDescription>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label htmlFor="if">From</Label>
                  <Input
                    id="if"
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="it">To</Label>
                  <Input
                    id="it"
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
                <Button onClick={loadIncome} disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Run
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.print()}
                  disabled={!income}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!income ? (
                <div className="text-center py-10 text-muted-foreground">
                  {loading ? "Loading…" : "Run to view income statement"}
                </div>
              ) : (
                <div className="space-y-4">
                  <Section
                    title="Revenue"
                    rows={income.revenues}
                    total={income.total_revenue}
                  />
                  <Section
                    title="Expenses"
                    rows={income.expenses}
                    total={income.total_expense}
                  />
                  <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
                    <div className="font-semibold">Net Income</div>
                    <div
                      className={`font-mono text-lg ${
                        income.net_income >= 0
                          ? "text-emerald-600 dark:text-emerald-300"
                          : "text-rose-600 dark:text-rose-300"
                      }`}
                    >
                      {fmt(income.net_income)}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bs" className="mt-4">
          <Card className="shadow-card">
            <CardHeader className="flex flex-row items-end justify-between gap-3 space-y-0 flex-wrap">
              <div>
                <CardTitle className="text-lg">Balance Sheet</CardTitle>
                <CardDescription>
                  Snapshot of assets, liabilities and equity as of a date.
                </CardDescription>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label htmlFor="bsdate">As of</Label>
                  <Input
                    id="bsdate"
                    type="date"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                </div>
                <Button onClick={loadBs} disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Run
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.print()}
                  disabled={!bs}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!bs ? (
                <div className="text-center py-10 text-muted-foreground">
                  {loading ? "Loading…" : "Run to view balance sheet"}
                </div>
              ) : (
                <div className="space-y-4">
                  <Section
                    title="Assets"
                    rows={bs.assets}
                    total={bs.total_assets}
                  />
                  <Section
                    title="Liabilities"
                    rows={bs.liabilities}
                    total={bs.total_liabilities}
                  />
                  <Section
                    title="Equity"
                    rows={[
                      ...bs.equity,
                      {
                        account: {
                          id: -1,
                          code: "—",
                          name: "Current Period Earnings",
                          type: "equity" as const,
                          subtype: null,
                          normal_balance: "credit" as const,
                          parent_id: null,
                          is_active: true,
                          description: null,
                          created_at: null,
                          updated_at: null,
                        },
                        amount: bs.ytd_earnings,
                      },
                    ]}
                    total={bs.total_equity + bs.ytd_earnings}
                  />
                  <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
                    <div className="font-semibold">
                      Total Liabilities &amp; Equity
                    </div>
                    <div className="font-mono text-lg">
                      {fmt(bs.total_liabilities_and_equity)}
                    </div>
                  </div>
                  <div className="text-center">
                    {bs.balanced ? (
                      <Badge
                        variant="outline"
                        className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                      >
                        Balanced
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
                      >
                        Assets ≠ Liabilities + Equity
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface SectionRow {
  account: {
    id: number;
    code: string;
    name: string;
  };
  amount: number;
}

function Section({
  title,
  rows,
  total,
}: {
  title: string;
  rows: SectionRow[];
  total: number;
}) {
  // Paginate the account rows only — the "Total {title}" row is derived from
  // the full section total and stays visible on every page.
  const pg = usePagination(rows);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <div>
      <div className="text-sm font-semibold mb-1">{title}</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[110px]">Code</TableHead>
            <TableHead>Account</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                No activity.
              </TableCell>
            </TableRow>
          ) : (
            pg.pageItems.map((r) => (
              <TableRow key={`${r.account.id}-${r.account.code}`}>
                <TableCell className="font-mono">{r.account.code}</TableCell>
                <TableCell>{r.account.name}</TableCell>
                <TableCell className="text-right font-mono">
                  {fmt(r.amount)}
                </TableCell>
              </TableRow>
            ))
          )}
          <TableRow className="bg-muted/40 font-semibold">
            <TableCell colSpan={2} className="text-right">
              Total {title}
            </TableCell>
            <TableCell className="text-right font-mono">{fmt(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {rows.length > 0 ? (
        <Pagination
          page={pg.page}
          pageCount={pg.pageCount}
          total={pg.total}
          from={pg.from}
          to={pg.to}
          onPrev={pg.prev}
          onNext={pg.next}
        />
      ) : null}
    </div>
  );
}
